// ⚠️ WARNING: DUAL CALENDAR ARCHITECTURE ⚠️
// This is the STANDALONE calendar page at /organizer/calendar
// There is ANOTHER calendar component at: src/components/CalendarView.tsx
// Any changes to calendar logic MUST be applied to BOTH files!
// See: CALENDAR_ARCHITECTURE.md for details
// TODO: Refactor to single reusable component

// Calendar View - Events & Deadlines
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { loadNotebooks } from '@/lib/organizerRepository';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Plus,
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  Users,
  Video,
  FileText,
  MessageCircle,
  BookOpen,
  Target,
} from 'lucide-react';

type CalendarItem = {
  id: string;
  title: string;
  dueDate: string;
  category: string;
  course?: string;
  description?: string;
  notes?: string;
  status?: string;
};

const CATEGORY_COLORS: Record<string, { bg: string; text: string; icon: any }> = {
  discussion: { bg: 'bg-purple-500', text: 'text-purple-400', icon: MessageCircle },
  assignment: { bg: 'bg-amber-500', text: 'text-amber-400', icon: FileText },
  experiential: { bg: 'bg-green-500', text: 'text-green-400', icon: Target },
  journal: { bg: 'bg-slate-500', text: 'text-slate-400', icon: BookOpen },
  video: { bg: 'bg-cyan-500', text: 'text-cyan-400', icon: Video },
};

export const CalendarView: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [items, setItems] = useState<CalendarItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedDayEvents, setSelectedDayEvents] = useState<CalendarItem[]>([]);
  const [showDayModal, setShowDayModal] = useState(false);

  // Load data from notebooks
  useEffect(() => {
    if (!user?.id) return;

    const loadData = async () => {
      setLoading(true);
      try {
        const notebooks = await loadNotebooks(String(user.id));
        
        console.log('[CalendarView] Loaded notebooks:', notebooks.length);
        
        // Extract all items with due dates from all classes and categories
        const allItems: CalendarItem[] = [];
        
        // #region SYNC: Course Name Logic - MUST match OrganizerPage.tsx and components/CalendarView.tsx
        notebooks.forEach(notebook => {
          notebook.categories?.forEach((cat: any) => {
            // Determine course name: use category for personal items, notebook title for class items
            const isPersonalCategory = ['work', 'home', 'travel'].includes(cat.id);
            const courseName = isPersonalCategory ? cat.id.toUpperCase() : notebook.title;
            
            // Get items from upcoming bucket
            (cat.buckets?.upcoming || []).forEach((item: any) => {
              if (item.dueDate) {
                allItems.push({
                  id: String(item.id),
                  title: item.title,
                  dueDate: item.dueDate,
                  category: cat.id,
                  course: courseName, // Use WORK/HOME/TRAVEL or class name
                  description: item.description || item.notes,
                  notes: item.notes,
                  status: item.status
                });
              }
            });
            
            // Also include completed items (grayed out)
            (cat.buckets?.completed || []).forEach((item: any) => {
              if (item.dueDate) {
                allItems.push({
                  id: String(item.id),
                  title: item.title,
                  dueDate: item.dueDate,
                  category: cat.id,
                  course: courseName, // Use WORK/HOME/TRAVEL or class name
                  description: item.description || item.notes,
                  notes: item.notes,
                  status: 'completed'
                });
              }
            });
          });
        });
        // #endregion
        
        console.log('[CalendarView] Total items with due dates:', allItems.length);
        console.log('[CalendarView] March 25th items:', allItems.filter(i => i.dueDate.includes('2026-03-25')));
        
        // Sort by due date
        allItems.sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
        
        setItems(allItems);
      } catch (error) {
        console.error('Error loading calendar data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user?.id]);

  // Calendar generation
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  
  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
  
  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const today = new Date();
  const isToday = (day: number) => {
    return day === today.getDate() &&
           currentDate.getMonth() === today.getMonth() &&
           currentDate.getFullYear() === today.getFullYear();
  };

  const getEventsForDate = (day: number) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return items.filter(item => item.dueDate.startsWith(dateStr));
  };

  // Generate calendar days
  const calendarDays = [];
  for (let i = 0; i < firstDayOfMonth; i++) {
    calendarDays.push(null);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white pb-24 sm:pb-6">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black/80 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/organizer')}
                className="p-2 rounded-full hover:bg-white/10 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center">
                <CalendarIcon className="w-5 h-5 text-white" />
              </div>

              <div>
                <h1 className="text-xl sm:text-2xl font-bold">Calendar</h1>
                <p className="text-xs sm:text-sm text-slate-400">
                  {loading ? 'Loading...' : `${items.length} total items`}
                </p>
              </div>
            </div>

            <button className="p-2 sm:px-4 sm:py-2 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 font-bold hover:shadow-lg transition-all flex items-center gap-2">
              <Plus className="w-5 h-5" />
              <span className="hidden sm:inline">New Event</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        
        {/* Calendar Controls */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={previousMonth}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            
            <h2 className="text-xl sm:text-2xl font-bold min-w-[200px] text-center">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            
            <button
              onClick={nextMonth}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          <button
            onClick={() => setCurrentDate(new Date())}
            className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm transition-colors"
          >
            Today
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
          {/* Weekday Headers */}
          <div className="grid grid-cols-7 gap-px bg-white/10">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div
                key={day}
                className="bg-black/60 p-2 sm:p-3 text-center text-xs sm:text-sm font-semibold text-slate-400"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-px bg-white/10">
            {calendarDays.map((day, index) => {
              const dayEvents = day ? getEventsForDate(day) : [];
              return (
                <div
                  key={index}
                  onClick={() => {
                    if (day) {
                      const dayDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
                      setSelectedDate(dayDate);
                      setSelectedDayEvents(dayEvents);
                      setShowDayModal(true);
                    }
                  }}
                  className={`bg-black/40 min-h-[80px] sm:min-h-[100px] p-2 ${
                    day ? 'hover:bg-white/10 cursor-pointer' : ''
                  } transition-colors`}
                >
                  {day && (
                    <>
                      <div
                        className={`text-sm sm:text-base font-semibold mb-1 ${
                          isToday(day)
                            ? 'w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-amber-500 text-slate-900 flex items-center justify-center'
                            : 'text-white'
                        }`}
                      >
                        {day}
                      </div>
                      
                      {/* Event Dots */}
                      <div className="space-y-1">
                        {dayEvents.slice(0, 2).map((item) => {
                          const category = CATEGORY_COLORS[item.category] || CATEGORY_COLORS.discussion;
                          const isCompleted = item.status === 'completed';
                          return (
                            <div
                              key={item.id}
                              className={`text-xs truncate px-1 py-0.5 rounded ${category.bg}/20 ${category.text} border ${category.bg}/30 ${
                                isCompleted ? 'opacity-50 line-through' : ''
                              }`}
                            >
                              {item.title}
                            </div>
                          );
                        })}
                        {dayEvents.length > 2 && (
                          <div className="text-[10px] text-slate-400 px-1">
                            +{dayEvents.length - 2} more
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Upcoming Events List */}
        <section className="space-y-4">
          <h3 className="text-lg font-semibold">All Items ({items.length})</h3>
          
          {loading ? (
            <div className="text-center py-8 text-slate-400">Loading calendar items...</div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-slate-400">No items with due dates yet</div>
          ) : (
            <div className="space-y-3">
              {items.map((item) => {
                const category = CATEGORY_COLORS[item.category] || CATEGORY_COLORS.discussion;
                const Icon = category.icon;
                const isCompleted = item.status === 'completed';
                
                return (
                  <div
                    key={item.id}
                    className={`p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all ${
                      isCompleted ? 'opacity-60' : ''
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${category.bg} flex items-center justify-center flex-shrink-0`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <h4 className={`font-semibold text-base sm:text-lg mb-1 ${isCompleted ? 'line-through' : ''}`}>
                          {item.title}
                        </h4>
                        
                        {item.course && (
                          <p className="text-xs text-purple-300 mb-1">📁 {item.course}</p>
                        )}
                        
                        {item.description && (
                          <p className="text-sm text-slate-400 mb-2">{item.description}</p>
                        )}

                        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400">
                          <div className="flex items-center gap-1">
                            <CalendarIcon className="w-3 h-3" />
                            <span>{new Date(item.dueDate).toLocaleDateString()}</span>
                          </div>
                          
                          <div className={`px-2 py-0.5 rounded-full ${category.bg}/20 ${category.text} text-[10px] uppercase font-semibold`}>
                            {item.category}
                          </div>

                          {isCompleted && (
                            <span className="text-emerald-400">✓ Completed</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>

      {/* Day Modal */}
      {showDayModal && selectedDate && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-2 border-amber-400/60 rounded-2xl max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-600/20 to-orange-600/20 border-b border-amber-400/30 p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xl font-bold text-amber-100">
                  📅 {selectedDate.toDateString() === new Date().toDateString() ? "Today's Schedule" : "Schedule"}
                </h3>
                <button
                  onClick={() => setShowDayModal(false)}
                  className="w-8 h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
                >
                  ✕
                </button>
              </div>
              <p className="text-sm text-amber-200/80">
                {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
            </div>

            {/* Items List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {selectedDayEvents.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-6xl mb-3">🎉</div>
                  <p className="text-lg font-semibold text-white mb-2">No items due on this date!</p>
                  <p className="text-sm text-slate-400">This day is clear</p>
                </div>
              ) : (
                selectedDayEvents.map((item) => {
                  const category = CATEGORY_COLORS[item.category] || CATEGORY_COLORS.discussion;
                  const Icon = category.icon;
                  const isCompleted = item.status === 'completed';
                  
                  return (
                    <div
                      key={item.id}
                      className={`bg-black/40 border border-white/20 rounded-xl p-4 ${isCompleted ? 'opacity-60' : ''} hover:border-white/40 transition-all cursor-pointer`}
                      onClick={() => {
                        // Navigate to main dashboard
                        setShowDayModal(false);
                        navigate('/organizer');
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg ${category.bg} flex items-center justify-center flex-shrink-0`}>
                          <Icon className="w-5 h-5 text-white" />
                        </div>

                        <div className="flex-1 min-w-0">
                          <h4 className={`font-semibold text-base mb-1 ${isCompleted ? 'line-through text-emerald-300/70' : 'text-white'}`}>
                            {item.title}
                          </h4>
                          
                          {item.course && (
                            <p className="text-xs text-purple-300 mb-1">📁 {item.course}</p>
                          )}

                          {item.description && (
                            <p className="text-sm text-slate-400 mb-2">{item.description}</p>
                          )}

                          <div className="flex items-center gap-2 text-xs flex-wrap">
                            <span className={`px-2 py-0.5 rounded-full ${category.bg}/20 ${category.text} font-semibold`}>
                              {item.category}
                            </span>
                            {isCompleted && (
                              <span className="text-emerald-400">✓ Completed</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer Buttons */}
            <div className="border-t border-white/10 p-4 space-y-2">
              <button
                onClick={() => setShowDayModal(false)}
                className="w-full px-4 py-2 rounded-lg bg-white/10 text-slate-300 hover:bg-white/20 text-sm transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarView;
